import type {
  Contact,
  Funnel,
  Lead,
  LeadFieldDefinition,
  LeadFieldValue,
  Membership,
  MembershipStatus,
  PartyChannel,
  Priority,
  Stage,
  Tag,
  Task,
} from "../../lib/types";
import { civilDate, daysBetween } from "../notifications/feed";

/**
 * Modelo puro das listas de cadastros: Leads, Clientes e Lixeira.
 *
 * As três superfícies são a mesma lista com recortes diferentes, então filtro,
 * ordenação, colunas, indicadores e exportação moram aqui, fora do JSX. É
 * também o que mantém o contador da visão igual à lista exibida: os dois saem
 * da mesma chamada de `filterRows` (AGENTS §10).
 *
 * O "AI Score" mantém o rótulo do protótipo aprovado, mas o número é
 * determinístico: tem a fórmula publicada em `HEAT_EXPLANATION`, é recalculado
 * a partir do domínio e o "?" da coluna diz que não há modelo de IA envolvido.
 */

export type ListScope = "leads" | "clientes" | "lixeira";

export const TIME_ZONE = "America/Sao_Paulo";

/** Dias sem interação a partir dos quais o lead é considerado esfriando. */
export const COOLING_DAYS = 7;

/** Janela dos indicadores de crescimento, em dias; a comparação usa a anterior. */
export const GROWTH_WINDOW_DAYS = 30;

export const LEAD_VIEWS = [
  { id: "todos", label: "Todos" },
  { id: "quentes", label: "Quentes" },
  { id: "esfriando", label: "Esfriando" },
  { id: "sem-tarefa", label: "Sem tarefa" },
  { id: "perdidos", label: "Perdidos" },
] as const;

export type LeadViewId = (typeof LEAD_VIEWS)[number]["id"];

/** Clientes não têm visão "Perdidos": a venda já foi fechada. */
export const CLIENT_VIEWS = LEAD_VIEWS.filter((view) => view.id !== "perdidos");

export const TRASH_VIEWS = [
  { id: "todos", label: "Todos" },
  { id: "em-negociacao", label: "Em negociação" },
  { id: "perdidos", label: "Perdidos" },
] as const;

export type TrashViewId = (typeof TRASH_VIEWS)[number]["id"];

export type ViewId = LeadViewId | TrashViewId;

export const HEAT_BANDS = [
  { id: "todos", label: "Qualquer calor", min: 0, max: 100 },
  { id: "alto", label: "Alto (80+)", min: 80, max: 100 },
  { id: "medio", label: "Médio (60–79)", min: 60, max: 79 },
  { id: "baixo", label: "Baixo (até 59)", min: 0, max: 59 },
] as const;

export type HeatBandId = (typeof HEAT_BANDS)[number]["id"];

/**
 * Período de criação (Leads) ou de conversão (Clientes), como as fichas do
 * protótipo: "30 dias" é hoje mais os 29 anteriores. `custom` usa `from`/`to`.
 */
export const PERIOD_OPTIONS = [
  { id: "todos", label: "Qualquer data", days: null },
  { id: "hoje", label: "Hoje", days: 0 },
  { id: "semana", label: "7 dias", days: 6 },
  { id: "mes", label: "30 dias", days: 29 },
  { id: "tri", label: "Trimestre", days: 89 },
  { id: "custom", label: "Custom", days: null },
] as const;

export type PeriodId = (typeof PERIOD_OPTIONS)[number]["id"];

/** Ficha padrão das listas, como no protótipo. */
export const DEFAULT_PERIOD: PeriodId = "mes";

/** Intervalo civil coberto por uma ficha; `null` quando não há recorte. */
export function periodRange(
  period: PeriodId,
  now: string,
  custom?: { from: string | null; to: string | null },
): { from: string; to: string } | null {
  const today = civilDate(now, TIME_ZONE);
  if (period === "custom") {
    if (!custom?.from && !custom?.to) return null;
    return { from: custom.from ?? "0000-01-01", to: custom.to ?? today };
  }
  const option = PERIOD_OPTIONS.find((item) => item.id === period);
  if (!option || option.days === null) return null;
  return { from: shiftDay(today, -option.days), to: today };
}

/** Dia civil deslocado em `delta` dias, sem depender do fuso do navegador. */
export function shiftDay(day: string, delta: number): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

/** "18/08/2026 – 16/09/2026" */
export function rangeLabel(range: { from: string; to: string } | null): string {
  if (!range) return "Qualquer data";
  const br = (day: string) => day.split("-").reverse().join("/");
  return `${br(range.from)} – ${br(range.to)}`;
}

export const PAGE_SIZES = [25, 50, 100] as const;

export const DEFAULT_PAGE_SIZE = 50;

/** Peso de cada parcela do calor; a soma é 100 e está publicada na ajuda. */
export const HEAT_WEIGHTS = {
  interaction: 40,
  stage: 30,
  value: 20,
  priority: 10,
} as const;

/** Dias de silêncio a partir dos quais a parcela de interação zera. */
export const HEAT_INTERACTION_LIMIT = 11;

export const HEAT_EXPLANATION = [
  {
    term: "O que é",
    description:
      "Nota de 0 a 100 calculada pelo próprio sistema a partir dos dados do lead. Não há modelo de IA envolvido.",
  },
  {
    term: "Fórmula",
    description:
      "40 pontos por interação recente, perdendo 4 a cada dia de silêncio e zerando com 11 dias; 30 pontos pela posição da etapa dentro do funil; 20 pontos pelo valor potencial em relação ao maior valor do mesmo funil; 10 pontos pela prioridade, sendo alta 10, média 5 e baixa 0.",
  },
  {
    term: "Quando muda",
    description:
      "Recalculado a cada leitura. Registrar uma interação real, avançar de etapa, mudar o valor ou a prioridade altera a nota; nada fica gravado.",
  },
  {
    term: "Sem interação registrada",
    description: "A parcela de interação passa a contar a partir da data de criação do lead.",
  },
];

/**
 * Cor decorativa da origem: a mesma origem tem sempre a mesma cor (§11).
 *
 * Mora aqui porque a lista, o perfil e a prévia mostram o mesmo ponto colorido;
 * duas tabelas de cor davam duas cores para a mesma origem.
 */
const SOURCE_COLORS = ["#5b8dff", "#f272a8", "#8e85ff", "#46d392", "#f5b860", "#4adec9", "#ff8a5c"];

export function sourceColor(source: string): string {
  let hash = 0;
  for (const char of source.toLowerCase()) hash = (hash * 31 + char.charCodeAt(0)) % SOURCE_COLORS.length;
  return SOURCE_COLORS[hash];
}

/** Faixa de cor do AI Score, usada pelo número e pela barra. */
export function heatColor(heat: number): string {
  return heat >= 80 ? "var(--good)" : heat >= 60 ? "var(--accent)" : "var(--warn)";
}

export interface LeadRow {
  lead: Lead;
  membership?: Membership;
  funnelId?: string;
  funnelName: string;
  stageName: string;
  stageColor: string;
  /** `none` quando o lead não tem participação, o que a lista precisa mostrar. */
  status: MembershipStatus | "none";
  /** "Cliente" quando a venda fechou, "Perdido" na perda, senão a etapa. */
  statusLabel: string;
  isClient: boolean;
  heat: number;
  pendingTasks: number;
  /** Dias desde a última interação real; usa a criação quando nunca houve uma. */
  quietDays: number;
  city: string;
  tagNames: string[];
}

export interface RowsInput {
  leads: readonly Lead[];
  memberships: readonly Membership[];
  stages: readonly Stage[];
  funnels: readonly Funnel[];
  tags: readonly Tag[];
  tasks: readonly Task[];
  /**
   * Definições e valores do formulário do funil. Cidade e estado são campos do
   * funil, não campos-base (§7.1): a coluna os lê daqui quando a coluna legada
   * do lead está vazia.
   */
  leadFields?: readonly LeadFieldDefinition[];
  leadFieldValues?: readonly LeadFieldValue[];
  /** Instante de referência; sempre injetado para o cálculo ser testável. */
  now: string;
}

function priorityPoints(priority: Priority): number {
  return priority === "high" ? 10 : priority === "medium" ? 5 : 0;
}

export function priorityLabel(priority: Priority): string {
  return priority === "high" ? "Alta" : priority === "medium" ? "Média" : "Baixa";
}

export function priorityTone(priority: Priority): string {
  return priority === "high"
    ? "var(--crit)"
    : priority === "medium"
      ? "var(--warn)"
      : "var(--faint)";
}

/** Dias civis de silêncio; sem interação registrada, conta desde a criação. */
export function quietDaysOf(lead: Lead, now: string): number {
  const reference = lead.lastInteractionAt ?? lead.createdAt;
  return Math.max(0, daysBetween(civilDate(reference, TIME_ZONE), civilDate(now, TIME_ZONE)));
}

export interface HeatInput {
  lead: Lead;
  membership?: Membership;
  stages: readonly Stage[];
  /** Maior valor potencial entre os leads do mesmo funil; normaliza a parcela. */
  funnelTopValue: number;
  now: string;
}

export function leadHeat({ lead, membership, stages, funnelTopValue, now }: HeatInput): number {
  const silence = quietDaysOf(lead, now);
  const interaction =
    silence >= HEAT_INTERACTION_LIMIT ? 0 : Math.max(0, HEAT_WEIGHTS.interaction - silence * 4);

  const funnelStages = membership
    ? stages
        .filter((stage) => stage.funnelId === membership.funnelId && !stage.archived)
        .sort((a, b) => a.order - b.order)
    : [];
  const index = funnelStages.findIndex((stage) => stage.id === membership?.stageId);
  const stagePoints =
    index < 0
      ? 0
      : funnelStages.length > 1
        ? (HEAT_WEIGHTS.stage * index) / (funnelStages.length - 1)
        : HEAT_WEIGHTS.stage;

  const value = Math.max(0, lead.potentialValue || 0);
  const valuePoints =
    funnelTopValue > 0 ? HEAT_WEIGHTS.value * Math.min(1, value / funnelTopValue) : 0;

  return Math.round(interaction + stagePoints + valuePoints + priorityPoints(lead.priority));
}

export function buildRows(input: RowsInput): LeadRow[] {
  const membershipByLead = new Map<string, Membership>();
  for (const membership of input.memberships) {
    if (!membershipByLead.has(membership.leadId)) membershipByLead.set(membership.leadId, membership);
  }

  const topValueByFunnel = new Map<string, number>();
  for (const lead of input.leads) {
    const membership = membershipByLead.get(lead.id);
    if (!membership) continue;
    const current = topValueByFunnel.get(membership.funnelId) ?? 0;
    topValueByFunnel.set(membership.funnelId, Math.max(current, lead.potentialValue || 0));
  }

  const pendingByLead = new Map<string, number>();
  for (const task of input.tasks) {
    if (task.status !== "pending") continue;
    // Tarefa interna não tem cadastro: não conta como tarefa de lead nenhum.
    if (!task.leadId) continue;
    pendingByLead.set(task.leadId, (pendingByLead.get(task.leadId) ?? 0) + 1);
  }

  // Valor de um campo do funil por chave, para os dados que não são campo-base.
  const fieldIdByKey = new Map<string, string>();
  for (const field of input.leadFields ?? []) {
    if (!fieldIdByKey.has(field.key)) fieldIdByKey.set(field.key, field.id);
  }
  const valueByLeadField = new Map<string, LeadFieldValue["value"]>();
  for (const value of input.leadFieldValues ?? []) {
    valueByLeadField.set(`${value.leadId}:${value.fieldId}`, value.value);
  }
  const fieldText = (leadId: string, key: string): string => {
    const fieldId = fieldIdByKey.get(key);
    if (!fieldId) return "";
    const value = valueByLeadField.get(`${leadId}:${fieldId}`);
    return typeof value === "string" ? value : "";
  };

  const stageById = new Map(input.stages.map((stage) => [stage.id, stage]));
  const funnelById = new Map(input.funnels.map((funnel) => [funnel.id, funnel]));
  const tagById = new Map(input.tags.map((tag) => [tag.id, tag]));

  return input.leads.map((lead) => {
    const membership = membershipByLead.get(lead.id);
    const stage = membership ? stageById.get(membership.stageId) : undefined;
    const funnel = membership ? funnelById.get(membership.funnelId) : undefined;
    const status = membership?.status ?? "none";
    return {
      lead,
      membership,
      funnelId: membership?.funnelId,
      funnelName: funnel?.name ?? "Sem funil",
      stageName: stage?.name ?? "Sem etapa",
      stageColor:
        lead.clientSince || status === "won"
          ? "var(--good)"
          : status === "lost"
            ? "var(--crit)"
            : (stage?.color ?? "var(--accent)"),
      status,
      // Cliente é quem teve o primeiro recebimento (§ Financeiro); a etiqueta
      // segue esse fato, não a situação da participação, porque um cliente
      // continua posicionado na esteira e a etapa não conta a mesma coisa.
      statusLabel:
        lead.clientSince || status === "won"
          ? "Cliente"
          : status === "lost"
            ? "Perdido"
            : (stage?.name ?? "Sem etapa"),
      isClient: Boolean(lead.clientSince),
      heat: leadHeat({
        lead,
        membership,
        stages: input.stages,
        funnelTopValue: membership ? (topValueByFunnel.get(membership.funnelId) ?? 0) : 0,
        now: input.now,
      }),
      pendingTasks: pendingByLead.get(lead.id) ?? 0,
      quietDays: quietDaysOf(lead, input.now),
      city: [lead.city || fieldText(lead.id, "city"), lead.state || fieldText(lead.id, "state")]
        .filter(Boolean)
        .join(" · "),
      tagNames: lead.tagIds
        .map((id) => tagById.get(id)?.name)
        .filter((name): name is string => Boolean(name)),
    };
  });
}

/** Recorte de base de cada superfície, antes das visões e dos filtros. */
export function scopeRows(rows: readonly LeadRow[], scope: ListScope): LeadRow[] {
  if (scope === "lixeira") return rows.filter((row) => row.lead.archived);
  if (scope === "clientes") return rows.filter((row) => !row.lead.archived && row.isClient);
  return rows.filter((row) => !row.lead.archived && !row.isClient);
}

export function matchesView(row: LeadRow, view: ViewId): boolean {
  switch (view) {
    case "quentes":
      return row.heat >= 80 && row.status !== "lost";
    case "esfriando":
      return row.quietDays >= COOLING_DAYS && row.status !== "lost";
    case "sem-tarefa":
      return row.pendingTasks === 0 && row.status !== "lost";
    case "perdidos":
      return row.status === "lost";
    case "em-negociacao":
      return row.status === "active";
    default:
      return true;
  }
}

export function comparable(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export interface FilterInput {
  rows: readonly LeadRow[];
  view: ViewId;
  query: string;
  funnelId: string | null;
  priority: Priority | null;
  heatBand: HeatBandId;
  period: PeriodId;
  /** Limites civis da ficha `custom`; ignorados nas demais. */
  from?: string | null;
  to?: string | null;
  /** Dias mínimos sem interação real ("Sem interação: 7 dias ou mais"). */
  quietDays?: number | null;
  /** Lixeira: só cadastros removidos nos últimos N dias (data de remoção conhecida). */
  removedWithinDays?: number | null;
  minValue: number | null;
  maxValue: number | null;
  now: string;
}

export function filterRows(input: FilterInput): LeadRow[] {
  const query = comparable(input.query.trim());
  const band = HEAT_BANDS.find((item) => item.id === input.heatBand) ?? HEAT_BANDS[0];
  const range = periodRange(input.period, input.now, { from: input.from ?? null, to: input.to ?? null });
  // Clientes são recortados pela conversão; as demais listas, pela criação.
  const dateOf = (row: LeadRow) =>
    row.isClient && row.lead.clientSince ? row.lead.clientSince : row.lead.createdAt;

  return input.rows.filter((row) => {
    if (!matchesView(row, input.view)) return false;
    if (input.funnelId && row.funnelId !== input.funnelId) return false;
    if (input.priority && row.lead.priority !== input.priority) return false;
    if (row.heat < band.min || row.heat > band.max) return false;
    if (input.minValue !== null && (row.lead.potentialValue || 0) < input.minValue) return false;
    if (input.maxValue !== null && (row.lead.potentialValue || 0) > input.maxValue) return false;
    if (range) {
      const day = civilDate(dateOf(row), TIME_ZONE);
      if (day < range.from || day > range.to) return false;
    }
    if (input.quietDays && row.quietDays < input.quietDays) return false;
    if (input.removedWithinDays) {
      if (!row.lead.archivedAt) return false;
      const age = daysBetween(civilDate(row.lead.archivedAt, TIME_ZONE), civilDate(input.now, TIME_ZONE));
      if (age < 0 || age > input.removedWithinDays - 1) return false;
    }
    if (query) {
      const haystack = comparable(
        [
          row.lead.name,
          row.lead.company,
          row.lead.role,
          row.lead.email,
          row.lead.city,
          row.lead.source,
          row.lead.segment,
          row.funnelName,
          row.statusLabel,
          ...row.tagNames,
        ]
          .filter(Boolean)
          .join(" "),
      );
      if (!haystack.includes(query)) return false;
    }
    return true;
  });
}

export type SortKey =
  | "name"
  | "stage"
  | "value"
  | "heat"
  | "source"
  | "priority"
  | "quiet"
  | "city"
  | "created"
  | "removed";

export type SortDirection = "asc" | "desc";

const PRIORITY_ORDER: Record<Priority, number> = { high: 0, medium: 1, low: 2 };

const COMPARATORS: Record<SortKey, (a: LeadRow, b: LeadRow) => number> = {
  name: (a, b) => a.lead.name.localeCompare(b.lead.name, "pt-BR"),
  stage: (a, b) =>
    a.statusLabel.localeCompare(b.statusLabel, "pt-BR") ||
    a.funnelName.localeCompare(b.funnelName, "pt-BR"),
  value: (a, b) => (a.lead.potentialValue || 0) - (b.lead.potentialValue || 0),
  heat: (a, b) => a.heat - b.heat,
  source: (a, b) => a.lead.source.localeCompare(b.lead.source, "pt-BR"),
  priority: (a, b) => PRIORITY_ORDER[b.lead.priority] - PRIORITY_ORDER[a.lead.priority],
  quiet: (a, b) => b.quietDays - a.quietDays,
  city: (a, b) => a.city.localeCompare(b.city, "pt-BR"),
  created: (a, b) => Date.parse(a.lead.createdAt) - Date.parse(b.lead.createdAt),
  // Sem data conhecida vai para o fim, em qualquer direção útil.
  removed: (a, b) =>
    (a.lead.archivedAt ? Date.parse(a.lead.archivedAt) : -Infinity) -
    (b.lead.archivedAt ? Date.parse(b.lead.archivedAt) : -Infinity),
};

export function sortRows(
  rows: readonly LeadRow[],
  key: SortKey,
  direction: SortDirection,
): LeadRow[] {
  const factor = direction === "asc" ? 1 : -1;
  // Desempate estável pelo nome: duas leituras da mesma seleção têm a mesma ordem.
  return [...rows].sort(
    (a, b) => COMPARATORS[key](a, b) * factor || a.lead.name.localeCompare(b.lead.name, "pt-BR"),
  );
}

export interface ColumnDefinition {
  id: string;
  label: string;
  width: number;
  sort?: SortKey;
  /** Alinhamento numérico à direita nas colunas de valor. */
  numeric?: boolean;
}

export const LIST_COLUMNS: ColumnDefinition[] = [
  { id: "lead", label: "Lead", width: 230, sort: "name" },
  { id: "stage", label: "Etapa / funil", width: 205, sort: "stage" },
  { id: "value", label: "Valor", width: 125, sort: "value", numeric: true },
  { id: "heat", label: "AI Score", width: 112, sort: "heat" },
  { id: "source", label: "Origem", width: 125, sort: "source" },
  { id: "priority", label: "Prioridade", width: 100, sort: "priority" },
  { id: "quiet", label: "Última interação", width: 120, sort: "quiet" },
  { id: "city", label: "Cidade", width: 160, sort: "city" },
];

/**
 * A lixeira não mostra a data de remoção porque o domínio não a registra:
 * arquivar apenas liga uma marca no lead. Em vez de exibir uma data inventada,
 * a coluna é a criação e a página explica a limitação (AGENTS §3, §18).
 */
export const TRASH_COLUMNS: ColumnDefinition[] = [
  { id: "lead", label: "Lead", width: 240, sort: "name" },
  { id: "stage", label: "Etapa / funil", width: 220, sort: "stage" },
  { id: "value", label: "Valor", width: 140, sort: "value", numeric: true },
  { id: "heat", label: "AI Score", width: 120, sort: "heat" },
  { id: "removed", label: "Movido para a lixeira", width: 180, sort: "removed" },
];

/** Colunas que a configuração não pode esconder, por identificarem a linha. */
export const LOCKED_COLUMNS = ["lead"];

export function columnsFor(scope: ListScope): ColumnDefinition[] {
  return scope === "lixeira" ? TRASH_COLUMNS : LIST_COLUMNS;
}

export function orderColumns(
  columns: readonly ColumnDefinition[],
  order: readonly string[],
  hidden: readonly string[],
): ColumnDefinition[] {
  const byId = new Map(columns.map((column) => [column.id, column]));
  const ordered = order
    .map((id) => byId.get(id))
    .filter((column): column is ColumnDefinition => Boolean(column));
  const missing = columns.filter((column) => !order.includes(column.id));
  return [...ordered, ...missing].filter((column) => !hidden.includes(column.id));
}

export interface Summary {
  total: number;
  active: number;
  attention: number;
  value: number;
}

/**
 * Indicadores da seleção. `attention` repete a regra da central de avisos:
 * sete dias sem interação real ou nenhuma tarefa pendente.
 */
export function summarize(rows: readonly LeadRow[], scope: ListScope): Summary {
  const active = scope === "clientes" ? [...rows] : rows.filter((row) => row.status === "active");
  const attention = active.filter((row) => row.quietDays >= COOLING_DAYS || row.pendingTasks === 0);
  const valueRows = scope === "lixeira" ? rows : active;
  return {
    total: rows.length,
    active: active.length,
    attention: attention.length,
    value: valueRows.reduce((sum, row) => sum + (row.lead.potentialValue || 0), 0),
  };
}

export interface GrowthResult {
  /** Registros datados dentro da janela corrente. */
  current: number;
  /** Mesma contagem na janela imediatamente anterior, de igual duração. */
  before: number;
  /** Seis intervalos de cinco dias, do mais antigo ao mais recente. */
  bins: number[];
}

/**
 * Compara eventos datados da janela corrente com a janela anterior de igual
 * duração. Não reconstrói estoque histórico: conta apenas as datas que os
 * registros atualmente selecionados carregam (AGENTS §10).
 */
export function growth(
  dates: ReadonlyArray<string | undefined>,
  now: string,
  weights?: ReadonlyArray<number>,
  /** Janela em dias (a ficha de período); a anterior tem a mesma duração. */
  windowDays: number = GROWTH_WINDOW_DAYS,
  /** Fim da janela (dia civil); hoje quando ausente. */
  endDay?: string,
): GrowthResult {
  const today = endDay ?? civilDate(now, TIME_ZONE);
  const span = Math.max(1, windowDays);
  const slotSize = Math.max(1, span / 6);
  const bins = Array.from({ length: 6 }, () => 0);
  let current = 0;
  let before = 0;

  dates.forEach((raw, index) => {
    if (!raw) return;
    const day = civilDate(raw, TIME_ZONE);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
    const age = daysBetween(day, today);
    const weight = weights ? (weights[index] ?? 0) : 1;
    if (age >= 0 && age <= span - 1) {
      current += weight;
      const slot = Math.min(5, Math.floor((span - 1 - age) / slotSize));
      bins[slot] += weight;
    } else if (age >= span && age <= span * 2 - 1) {
      before += weight;
    }
  });

  return { current, before, bins };
}

/** Texto do rodapé do indicador: porcentagem quando existe base anterior. */
export function deltaLabel(result: GrowthResult, formatValue: (value: number) => string): string {
  const diff = result.current - result.before;
  const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "↔";
  if (!result.before) return diff ? `${arrow}${formatValue(Math.abs(diff))}` : "↔ sem base anterior";
  const percent = Math.abs((diff / result.before) * 100).toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
  });
  return `${arrow}${percent}%`;
}

/** Participação de uma parte na seleção, para o rodapé dos indicadores. */
export function shareLabel(part: number, whole: number): string {
  if (!whole) return "Seleção vazia";
  return `${((100 * part) / whole).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% da seleção`;
}

export function paginate<T>(rows: readonly T[], page: number, size: number): T[] {
  const start = (page - 1) * size;
  return rows.slice(start, start + size);
}

export function pageCount(total: number, size: number): number {
  return Math.max(1, Math.ceil(total / size));
}

/**
 * As 22 colunas do arquivo, na ordem do protótipo. Cada grupo de dados da
 * exportação liga um subconjunto delas (`EXPORT_GROUPS`).
 */
export const EXPORT_HEADERS = [
  "ID",
  "Nome",
  "Empresa",
  "Função",
  "E-mail",
  "Telefone",
  "Site",
  "Instagram",
  "Cidade / UF",
  "Segmento",
  "Origem",
  "Funil",
  "Etapa",
  "Situação",
  "Valor do cadastro (R$)",
  "Prioridade",
  "Tags",
  "Data de cadastro",
  "Contatos: nomes",
  "Contatos: funções",
  "Contatos: e-mails",
  "Contatos: telefones",
] as const;

export type ExportGroupId = "identity" | "company" | "contact" | "commercial" | "people";

export const EXPORT_GROUPS: readonly {
  id: ExportGroupId;
  label: string;
  detail: string;
  columns: readonly number[];
}[] = [
  { id: "identity", label: "Identificação", detail: "Nome, ID e data de cadastro", columns: [0, 1, 17] },
  { id: "company", label: "Empresa", detail: "Empresa, função, site e segmento", columns: [2, 3, 6, 9] },
  { id: "contact", label: "Contato principal", detail: "E-mail, telefone, Instagram e cidade", columns: [4, 5, 7, 8] },
  {
    id: "commercial",
    label: "Dados comerciais",
    detail: "Origem, funil, etapa, valor e prioridade",
    columns: [10, 11, 12, 13, 14, 15, 16],
  },
  { id: "people", label: "Contatos adicionais", detail: "Nomes, funções, e-mails e telefones", columns: [18, 19, 20, 21] },
];

export const ALL_EXPORT_GROUPS: readonly ExportGroupId[] = EXPORT_GROUPS.map((group) => group.id);

/** Pessoas além do contato principal, que a exportação lista em colunas próprias. */
export interface ExportPeople {
  contacts: readonly Contact[];
  partyChannels: readonly PartyChannel[];
}

/**
 * A tabela da exportação — cabeçalho e linhas —, já recortada pelos grupos
 * escolhidos. A prévia e o arquivo saem da mesma tabela, então o que se vê
 * antes de baixar é o que o arquivo contém.
 */
export function exportTable(
  rows: readonly LeadRow[],
  groups: readonly ExportGroupId[] = ALL_EXPORT_GROUPS,
  people?: ExportPeople,
): string[][] {
  const situation = (row: LeadRow) =>
    row.isClient
      ? "Cliente"
      : row.status === "lost"
        ? "Perdido"
        : row.status === "none"
          ? "Sem participação"
          : "Em negociação";
  const extras = (lead: Lead) => {
    const ids = (lead.contactIds ?? []).filter((id) => id !== lead.primaryContactId);
    const list = ids
      .map((id) => people?.contacts.find((contact) => contact.id === id))
      .filter((contact): contact is Contact => Boolean(contact));
    const channel = (contact: Contact, type: PartyChannel["type"]) =>
      people?.partyChannels.find(
        (item) => item.contactId === contact.id && item.type === type && item.status === "active",
      )?.value ?? "";
    return {
      names: list.map((contact) => contact.name).join("\n"),
      roles: list.map((contact) => contact.role ?? "").join("\n"),
      emails: list.map((contact) => channel(contact, "email")).join("\n"),
      phones: list.map((contact) => channel(contact, "phone")).join("\n"),
    };
  };
  const lines = rows.map((row) => {
    const more = extras(row.lead);
    return [
      row.lead.id,
      row.lead.name,
      row.lead.company,
      row.lead.role,
      row.lead.email ?? "",
      row.lead.phone ?? "",
      row.lead.site ?? "",
      row.lead.instagram ?? "",
      row.city,
      row.lead.segment,
      row.lead.source,
      row.funnelName,
      row.stageName,
      situation(row),
      Number(row.lead.potentialValue || 0).toFixed(2).replace(".", ","),
      priorityLabel(row.lead.priority),
      row.tagNames.join(", "),
      civilDate(row.lead.createdAt, TIME_ZONE),
      more.names,
      more.roles,
      more.emails,
      more.phones,
    ];
  });
  const selected = new Set(
    EXPORT_GROUPS.filter((group) => groups.includes(group.id)).flatMap((group) => group.columns),
  );
  const keep = <T,>(line: readonly T[]) => line.filter((_, index) => selected.has(index));
  return [keep([...EXPORT_HEADERS]), ...lines.map(keep)];
}

/**
 * CSV da seleção inteira, não apenas da página visível.
 *
 * Células iniciadas por sinal de igual, mais, menos, arroba, tabulação ou
 * retorno recebem um apóstrofo antes: sem isso a planilha executaria o conteúdo
 * como fórmula (AGENTS §26.3). O BOM mantém os acentos legíveis no Excel.
 */
export function rowsToCsv(
  rows: readonly LeadRow[],
  groups: readonly ExportGroupId[] = ALL_EXPORT_GROUPS,
  people?: ExportPeople,
): string {
  const neutralize = (value: unknown) => {
    const text = String(value ?? "");
    return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  };
  const cell = (value: unknown) => `"${neutralize(value).replaceAll('"', '""')}"`;
  return `\uFEFF${exportTable(rows, groups, people)
    .map((line) => line.map(cell).join(";"))
    .join("\r\n")}`;
}

export function csvFileName(scope: ListScope, now: string): string {
  const noun = scope === "clientes" ? "clientes" : scope === "lixeira" ? "lixeira" : "leads";
  return `cosmos-${noun}-${civilDate(now, TIME_ZONE)}.csv`;
}
